/**
 * Web Vitals Dashboard Component
 *
 * Displays Core Web Vitals metrics in a floating panel.
 * Shows LCP, FID, CLS, FCP, TTFB with color-coded ratings.
 *
 * This component works in both development and production,
 * but is only visible when explicitly enabled.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  useTheme,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Speed,
  ExpandMore,
  ExpandLess,
  Close,
} from '@mui/icons-material';
import { getLatestMetrics } from '../../utils/performanceMonitoring';

interface WebVitalsDashboardProps {
  /** Whether the dashboard is visible */
  visible?: boolean;
  /** Callback when user closes the dashboard */
  onClose?: () => void;
  /** Position on screen */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

// Web Vitals descriptions for tooltips
const VITAL_DESCRIPTIONS: Record<string, string> = {
  LCP: 'Largest Contentful Paint - measures loading performance. Should be < 2.5s',
  FID: 'First Input Delay - measures interactivity. Should be < 100ms',
  CLS: 'Cumulative Layout Shift - measures visual stability. Should be < 0.1',
  FCP: 'First Contentful Paint - time to first visual. Should be < 1.8s',
  TTFB: 'Time to First Byte - server response time. Should be < 800ms',
  INP: 'Interaction to Next Paint - measures responsiveness. Should be < 200ms',
};

// Format metric value based on type
const formatValue = (name: string, value: number): string => {
  if (name === 'CLS') {
    return value.toFixed(3);
  }
  return `${Math.round(value)}ms`;
};

const WebVitalsDashboard: React.FC<WebVitalsDashboardProps> = ({
  visible = true,
  onClose,
  position = 'bottom-right',
}) => {
  const theme = useTheme();
  const [metrics, setMetrics] = useState(getLatestMetrics());
  const [expanded, setExpanded] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Refresh metrics periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(getLatestMetrics());
      setLastUpdate(new Date());
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  if (!visible) {
    return null;
  }

  const positionStyles = {
    'bottom-right': { bottom: 16, right: 16 },
    'bottom-left': { bottom: 16, left: 16 },
    'top-right': { top: 80, right: 16 },
    'top-left': { top: 80, left: 16 },
  };

  const getRatingColor = (rating: string | undefined) => {
    switch (rating) {
      case 'good':
        return 'success';
      case 'needs-improvement':
        return 'warning';
      case 'poor':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRatingEmoji = (rating: string | undefined) => {
    switch (rating) {
      case 'good':
        return '🟢';
      case 'needs-improvement':
        return '🟡';
      case 'poor':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const vitalsOrder = ['LCP', 'FID', 'CLS', 'FCP', 'TTFB', 'INP'];
  const displayedMetrics = vitalsOrder
    .filter((name) => metrics[name])
    .map((name) => ({ name, ...metrics[name]! }));

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        ...positionStyles[position],
        width: expanded ? 280 : 'auto',
        zIndex: 9999,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(0,0,0,0.02)',
          borderBottom: expanded ? `1px solid ${theme.palette.divider}` : 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Speed sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13 }}>
            Web Vitals
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 1.5 }}>
          {displayedMetrics.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                py: 2,
                color: theme.palette.text.secondary,
                fontSize: 12,
              }}
            >
              Collecting metrics...
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {displayedMetrics.map(({ name, value, rating }) => (
                <Tooltip key={name} title={VITAL_DESCRIPTIONS[name] || ''} placement="left">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.03)'
                        : 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 12 }}>
                        {getRatingEmoji(rating)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, fontSize: 12, minWidth: 40 }}
                      >
                        {name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 11,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {formatValue(name, value)}
                      </Typography>
                      <Chip
                        size="small"
                        label={rating || 'pending'}
                        color={getRatingColor(rating) as any}
                        sx={{ fontSize: 9, height: 18, minWidth: 50 }}
                      />
                    </Box>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}

          {/* Footer */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 1.5,
              color: theme.palette.text.disabled,
              fontSize: 10,
            }}
          >
            Updated: {lastUpdate.toLocaleTimeString()}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default WebVitalsDashboard;
